<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { AdaptiveMetricRoi, NormalizedRect, OcrInputSnapshot } from '@/domain/types'

const props = defineProps<{
  snapshot?: OcrInputSnapshot
  video: HTMLVideoElement
  roi: AdaptiveMetricRoi
  textLabel: string
  barLabel: string
}>()

const canvas = ref<HTMLCanvasElement | null>(null)
let animation = 0
let lastDraw = 0
const aspect = computed(() => {
  if (props.snapshot) return `${props.snapshot.source.width} / ${props.snapshot.source.height}`
  const width = props.video.videoWidth || 16
  const height = props.video.videoHeight || 9
  return `${Math.max(1, props.roi.searchRect.width * width)} / ${Math.max(1, props.roi.searchRect.height * height)}`
})

function relativeStyle(rect: NormalizedRect | undefined): Record<string, string> {
  if (!rect) return {}
  const search = props.roi.searchRect
  return {
    left: `${Math.max(0, (rect.x - search.x) / search.width) * 100}%`,
    top: `${Math.max(0, (rect.y - search.y) / search.height) * 100}%`,
    width: `${Math.min(1, rect.width / search.width) * 100}%`,
    height: `${Math.min(1, rect.height / search.height) * 100}%`,
  }
}

function draw(now: number): void {
  if (canvas.value && now - lastDraw >= 200 && props.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    const videoWidth = props.video.videoWidth
    const videoHeight = props.video.videoHeight
    if (videoWidth && videoHeight) {
      const search = props.roi.searchRect
      const sourceX = Math.round(search.x * videoWidth)
      const sourceY = Math.round(search.y * videoHeight)
      const sourceWidth = Math.max(1, Math.round(search.width * videoWidth))
      const sourceHeight = Math.max(1, Math.round(search.height * videoHeight))
      canvas.value.width = sourceWidth
      canvas.value.height = sourceHeight
      canvas.value.getContext('2d')?.drawImage(props.video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, sourceWidth, sourceHeight)
      lastDraw = now
    }
  }
  animation = requestAnimationFrame(draw)
}

function refresh(): void {
  cancelAnimationFrame(animation)
  if (props.snapshot && canvas.value) {
    const source = props.snapshot.source
    canvas.value.width = source.width
    canvas.value.height = source.height
    canvas.value.getContext('2d')?.putImageData(source, 0, 0)
  } else {
    lastDraw = 0
    animation = requestAnimationFrame(draw)
  }
}
onMounted(refresh)
watch(() => props.snapshot, refresh, { flush: 'post' })
onBeforeUnmount(() => cancelAnimationFrame(animation))
</script>

<template>
  <figure class="issue-preview" :style="{ aspectRatio: aspect }">
    <canvas ref="canvas" />
    <div v-if="!snapshot && roi.status === 'adapted'" class="preview-outline text" :style="relativeStyle(roi.textRect)"><span>{{ textLabel }}</span></div>
    <div v-if="!snapshot && roi.status === 'adapted' && roi.barRect" class="preview-outline bar" :style="relativeStyle(roi.barRect)"><span>{{ barLabel }}</span></div>
  </figure>
</template>

<style scoped>
.issue-preview { position: relative; min-height: 4rem; max-height: 9rem; margin: 0; overflow: hidden; border: 0.125rem dashed #79c0ff; border-radius: 0.375rem; background: #090d0e; }
canvas { display: block; width: 100%; height: 100%; object-fit: fill; image-rendering: pixelated; }
.preview-outline { position: absolute; border: 0.125rem solid #59d6a5; border-radius: 0.1875rem; box-shadow: 0 0 0 0.0625rem rgb(0 0 0 / 45%); }
.preview-outline.bar { border-color: #e4bf54; }
.preview-outline span { position: absolute; left: -0.125rem; bottom: calc(100% + 0.1875rem); padding: 0.125rem 0.3125rem; border-radius: 0.1875rem; background: #59d6a5; color: #071c14; font-size: 0.625rem; line-height: 1.2; font-weight: 750; white-space: nowrap; }
.preview-outline.bar span { background: #e4bf54; color: #211a05; }
</style>
